function enhanceSelect(select: HTMLSelectElement): void {
    if (select.dataset.appSelectReady === 'true') {
        return
    }

    const dialog = select.closest<HTMLDialogElement>('dialog')
    const formGroup = select.closest<HTMLElement>('.form-group')
    const label = formGroup?.querySelector<HTMLLabelElement>(`label[for="${select.id}"]`)
    if (!formGroup || !select.id) {
        return
    }

    select.dataset.appSelectReady = 'true'

    const wrapper = document.createElement('div')
    wrapper.className = 'app-select-enhanced'
    select.parentElement?.insertBefore(wrapper, select)
    wrapper.append(select)

    const trigger = document.createElement('button')
    trigger.type = 'button'
    trigger.id = `${select.id}-button`
    trigger.className = 'app-select-trigger'
    trigger.setAttribute('role', 'combobox')
    trigger.setAttribute('aria-haspopup', 'listbox')
    trigger.setAttribute('aria-expanded', 'false')
    trigger.setAttribute('aria-controls', `${select.id}-options`)

    const labelText = label?.textContent?.trim()
    if (label) {
        label.htmlFor = trigger.id
        if (!label.id) {
            label.id = `${select.id}-label`
        }
        trigger.setAttribute('aria-labelledby', label.id)
    } else if (labelText) {
        trigger.setAttribute('aria-label', labelText)
    }

    const value = document.createElement('span')
    value.className = 'app-select-value'
    const chevron = document.createElement('span')
    chevron.className = 'app-select-chevron'
    chevron.setAttribute('aria-hidden', 'true')
    trigger.append(value, chevron)

    const menu = document.createElement('div')
    menu.id = `${select.id}-popup`
    menu.className = 'app-select-menu'
    menu.hidden = true
    ;(dialog ?? document.body).append(menu)

    const search = select.options.length >= 8 ? document.createElement('input') : null
    if (search) {
        search.type = 'search'
        search.className = 'app-select-search'
        search.placeholder = 'Поиск…'
        search.setAttribute('aria-label', labelText ? `Поиск: ${labelText}` : 'Поиск вариантов')
        menu.append(search)
    }

    const listbox = document.createElement('div')
    listbox.id = `${select.id}-options`
    listbox.className = 'app-select-list'
    listbox.setAttribute('role', 'listbox')
    menu.append(listbox)

    const options = Array.from(select.options).map((nativeOption) => {
        const option = document.createElement('button')
        option.type = 'button'
        option.className = 'app-select-option'
        option.setAttribute('role', 'option')
        option.dataset.value = nativeOption.value
        option.textContent = nativeOption.textContent?.trim() || (nativeOption.value === '' ? 'Без выбора' : '')
        option.disabled = nativeOption.disabled
        option.setAttribute('aria-selected', String(nativeOption.selected))
        listbox.append(option)
        return option
    })

    function syncSelection(): void {
        const selectedOption = select.selectedOptions.item(0)
        value.textContent = selectedOption?.value === ''
            ? select.dataset.placeholder ?? 'Выберите…'
            : selectedOption?.textContent?.trim() ?? ''
        options.forEach((option) => {
            const selected = option.dataset.value === select.value
            option.setAttribute('aria-selected', String(selected))
            option.classList.toggle('is-selected', selected)
        })
        trigger.disabled = select.disabled
        trigger.setAttribute('aria-invalid', select.getAttribute('aria-invalid') ?? 'false')
    }

    function getEnabledOptions(): HTMLButtonElement[] {
        return options.filter((option) => !option.disabled && !option.hidden)
    }

    function filterOptions(): void {
        const query = search?.value.trim().toLocaleLowerCase() ?? ''
        options.forEach((option) => {
            option.hidden = !option.textContent?.toLocaleLowerCase().includes(query)
        })

        let emptyState = listbox.querySelector<HTMLElement>('.app-select-empty')
        if (!getEnabledOptions().length) {
            if (!emptyState) {
                emptyState = document.createElement('div')
                emptyState.className = 'app-select-empty'
                emptyState.textContent = 'Ничего не найдено'
                listbox.append(emptyState)
            }
        } else {
            emptyState?.remove()
        }
    }

    function positionMenu(): void {
        const rect = trigger.getBoundingClientRect()
        const menuHeight = menu.getBoundingClientRect().height
        const spaceBelow = window.innerHeight - rect.bottom
        const top = spaceBelow >= menuHeight + 8 || spaceBelow >= rect.top
            ? rect.bottom + 4
            : rect.top - menuHeight - 4
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - rect.width - 8))

        menu.style.top = `${Math.max(8, top)}px`
        menu.style.left = `${left}px`
        menu.style.width = `${rect.width}px`
    }

    function closeMenu(returnFocus = false): void {
        if (menu.hidden) {
            return
        }

        menu.hidden = true
        trigger.setAttribute('aria-expanded', 'false')
        if (search) {
            search.value = ''
            filterOptions()
        }
        document.removeEventListener('pointerdown', handleOutsidePointer, true)
        dialog?.removeEventListener('scroll', positionMenu, true)
        window.removeEventListener('resize', positionMenu)
        window.removeEventListener('scroll', positionMenu, true)
        if (returnFocus) {
            trigger.focus()
        }
    }

    function handleOutsidePointer(event: PointerEvent): void {
        const target = event.target
        if (target instanceof Node && (wrapper.contains(target) || menu.contains(target))) {
            return
        }
        closeMenu()
    }

    function openMenu(focusLast = false, focusSearch = false): void {
        if (trigger.disabled) {
            return
        }

        menu.hidden = false
        trigger.setAttribute('aria-expanded', 'true')
        filterOptions()
        positionMenu()
        document.addEventListener('pointerdown', handleOutsidePointer, true)
        dialog?.addEventListener('scroll', positionMenu, true)
        window.addEventListener('resize', positionMenu)
        window.addEventListener('scroll', positionMenu, true)

        if (focusSearch) {
            search?.focus()
            return
        }

        const enabledOptions = getEnabledOptions()
        const focusOption = focusLast ? enabledOptions.at(-1) : enabledOptions[0]
        focusOption?.focus()
    }

    trigger.addEventListener('click', () => {
        if (menu.hidden) {
            openMenu(false, Boolean(search))
        } else {
            closeMenu()
        }
    })

    trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            openMenu(event.key === 'ArrowUp')
        } else if (event.key === 'Escape' && !menu.hidden) {
            event.preventDefault()
            closeMenu()
        }
    })

    search?.addEventListener('input', () => {
        filterOptions()
        positionMenu()
    })
    search?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault()
            getEnabledOptions()[0]?.focus()
        } else if (event.key === 'Escape') {
            event.preventDefault()
            closeMenu(true)
        } else if (event.key === 'Enter') {
            const firstOption = getEnabledOptions()[0]
            if (firstOption) {
                event.preventDefault()
                firstOption.click()
            }
        } else if (event.key === 'Tab') {
            closeMenu()
        }
    })

    options.forEach((option) => {
        option.addEventListener('click', () => {
            if (option.disabled) {
                return
            }
            select.value = option.dataset.value ?? ''
            select.dispatchEvent(new Event('change', {bubbles: true}))
            closeMenu(true)
        })

        option.addEventListener('keydown', (event) => {
            const enabledOptions = getEnabledOptions()
            const enabledIndex = enabledOptions.indexOf(option)
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault()
                const direction = event.key === 'ArrowDown' ? 1 : -1
                const nextIndex = (enabledIndex + direction + enabledOptions.length) % enabledOptions.length
                enabledOptions[nextIndex]?.focus()
            } else if (event.key === 'Home') {
                event.preventDefault()
                enabledOptions[0]?.focus()
            } else if (event.key === 'End') {
                event.preventDefault()
                enabledOptions.at(-1)?.focus()
            } else if (event.key === 'Escape') {
                event.preventDefault()
                closeMenu(true)
            } else if (event.key === 'Tab') {
                closeMenu()
            }
        })
    })

    select.addEventListener('change', syncSelection)
    dialog?.addEventListener('cancel', (event) => {
        if (!menu.hidden) {
            event.preventDefault()
            closeMenu()
        }
    })

    label?.addEventListener('click', (event) => {
        event.preventDefault()
        trigger.focus()
    })

    syncSelection()
    filterOptions()
    const describedBy = select.getAttribute('aria-describedby')
    if (describedBy) {
        trigger.setAttribute('aria-describedby', describedBy)
    }
    select.classList.add('app-select-source')
    select.tabIndex = -1
    select.setAttribute('aria-hidden', 'true')
    wrapper.append(trigger)
}

export function enhanceAppSelects(root: ParentNode): void {
    root.querySelectorAll<HTMLSelectElement>('select.app-select-twe').forEach(enhanceSelect)
}
